import * as translator from '../../../../utils/translator';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomerAddressCreation, createCustomerAddressCreationSchema } from './schemas';
import { useEffect, useMemo, useState } from 'react';
import { LuBuilding2, LuHome, LuBriefcase, LuBedDouble, LuMapPin } from 'react-icons/lu';
import Select from '../../../../components/Select/Select';
import { dailingCodes } from '../../../../utils/consts';
import { validatePhoneDE } from '../../../../utils/fieldsValidator';


type DeliveryAddressPersonalAddressFormProps = {
    onSubmit: (formData: CustomerAddressCreation) => void;
    formID: string;
    language: string;
    defaultFormValues?: any;
    countryOptions: any[];
};

const DeliveryAddressPersonalAddressForm: React.FC<DeliveryAddressPersonalAddressFormProps> = ({
    onSubmit,
    formID,
    language,
    defaultFormValues,
    countryOptions
}) => {
    const customerAddressSchema = createCustomerAddressCreationSchema(language)
    const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm<CustomerAddressCreation>({
        resolver: zodResolver(customerAddressSchema),
        mode: "onSubmit",
        defaultValues: defaultFormValues,
    });

    const [selectedCountry, setSelectedCountry] = useState<{ id: string; value: string } | undefined>(undefined);
    const [selectedLocationType, setSelectedLocationType] = useState<{ value: string; code: string; icon: JSX.Element } | undefined>(undefined);
    const [dailingCode, setDailingCode] = useState<string | undefined>("+49");

    const locationTypeOptions = useMemo(() => ([
        { value: translator.translate(language, "my_account", "DeliveryAddressV2/addressSubTypeApartment") || "Apartment", code: "apartment", icon: <LuBuilding2 size={14} /> },
        { value: translator.translate(language, "my_account", "DeliveryAddressV2/addressSubTypeHouse") || "House", code: "house", icon: <LuHome size={14} /> },
        { value: translator.translate(language, "my_account", "DeliveryAddressV2/addressSubTypeWork") || "Office", code: "office", icon: <LuBriefcase size={14} /> },
        { value: translator.translate(language, "my_account", "DeliveryAddressV2/addressSubTypeHotel") || "Hotel", code: "hotel", icon: <LuBedDouble size={14} /> },
        { value: translator.translate(language, "my_account", "DeliveryAddressV2/addressSubTypeOther") || "Other", code: "other", icon: <LuMapPin size={14} /> },
    ]), [language]);

    useEffect(() => {
        // Reset form when address attributes change
        if (defaultFormValues) {
            reset(defaultFormValues);
            setSelectedCountry(countryOptions.find(option => option.id === defaultFormValues.country_id) || countryOptions[0]);
            const locationType = locationTypeOptions.find(option => option.code === defaultFormValues.address_sub_type) || locationTypeOptions[0];
            setSelectedLocationType(locationType);
            setValue("address_sub_type", locationType.code);
        }
    }, [defaultFormValues, reset, countryOptions, locationTypeOptions]);
    
    useEffect(() => {
        if(selectedCountry?.id) {
            const dailingCode = dailingCodes[selectedCountry.id.toUpperCase() as keyof typeof dailingCodes];
            if(dailingCode) {
                setDailingCode(dailingCode);
            }
        }
    }, [selectedCountry]);
    
    const handleCountryChange = ({ option }: any) => {
        setValue("country_id", option.id, { shouldValidate: true });
        setSelectedCountry(option);
    };

    const handleLocationTypeChange = ({ option }: any) => {
        setValue("address_sub_type", option.code, { shouldValidate: true });
        setSelectedLocationType(option);
    };

    const inputClasses = (fieldName: keyof CustomerAddressCreation) => `
        w-full px-4 py-3 border border-solid
        ${errors[fieldName]
            ? 'border-red-500'
            : 'border-[#E5E5E5]'} 
        rounded-md focus:outline-none focus:ring-2 focus:ring-[#269C6E]
        placeholder:text-gray-500 text-sm
    `;

    return (
        <div id="address-modal" className="[&_input]:border-solid [&_input[type=button]]:border-[#269C6E] max-w-md w-full rounded-md rounded-tl-3xl rounded-br-3xl">

            {/* Form */}
            <form id={formID} className='flex flex-col gap-y-4 w-full max-w-md mx-auto' onSubmit={handleSubmit(onSubmit)}>
                <div id='address-form-container' className='flex flex-col gap-y-4 px-0 md:px-4'>
                    <div id="address-sub-type-container" className="[&_.credential-input-mandatory-astrix]:hidden">
                        <Select
                            id="address-sub-type"
                            options={locationTypeOptions as never[]}
                            initialValue={selectedLocationType}
                            onChangeCallback={handleLocationTypeChange}
                            placeholder={translator.translate(language, "my_account", "DeliveryAddressV2/addressSubTypeLabel")}
                            errorMode={false}
                            style={{
                                border: "1px solid #E5E5E5",
                                height: "48px",
                                width: "100%",
                                borderRadius: "7px",
                                backgroundColor: "white",
                                margin: "0px"
                            }}
                            label={translator.translate(language, "my_account", "DeliveryAddressV2/addressSubTypeLabel")}
                        />
                    </div>
                    <div id="address-name-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-0 md:gap-x-4">
                        <div id="address-firstname-container">
                            <input
                                id="address-firstname"
                                {...register("first_name")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `DeliveryAddressV2/firstName`)}
                                className={inputClasses("first_name")}
                            />
                            {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name.message}</p>}
                        </div>
                        <div id="address-lastname-container">
                            <input
                                id="address-lastname"
                                {...register("last_name")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `DeliveryAddressV2/lastName`)}
                                className={inputClasses("last_name")}
                            />
                            {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name.message}</p>}
                        </div>
                    </div>

                    <div id="address-street-container">
                        <input
                            id="address-street"
                            {...register("address")}
                            type="text"
                            placeholder={translator.translate(language, "my_account", `DeliveryAddressV2/street`)}
                            className={inputClasses("address")}
                        />
                        {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>}
                    </div>

                    <div id="address-location-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-3 md:gap-y-0 md:gap-x-4">
                        <div id="address-zip-container">
                            <input
                                id="address-zip"
                                {...register("zip")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `DeliveryAddressV2/zip`)}
                                className={inputClasses("zip")}
                            />
                            {errors.zip && <p className="text-red-500 text-xs mt-1">{errors.zip.message}</p>}
                        </div>
                        <div id="address-city-container">
                            <input
                                id="address-city"
                                {...register("city")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `DeliveryAddressV2/city`)}
                                className={inputClasses("city")}
                            />
                            {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city.message}</p>}
                        </div>
                    </div>

                    <div id="address-country-container">
                        <Select
                            id="address-country"
                            options={countryOptions as never[]}
                            initialValue={selectedCountry}
                            onChangeCallback={handleCountryChange}
                            placeholder="Country"
                            errorMode={false}
                            disabled={countryOptions.length === 1}
                            style={{
                                border: "1px solid #E5E5E5",
                                height: "44px",
                                width: "100%",
                                borderRadius: "7px",
                                backgroundColor: "white",
                                margin: "0px"
                            }}
                        />
                    </div>

                    <div id="address-company-container">
                        <input
                            id="address-company"
                            {...register("company")}
                            type="text"
                            placeholder={translator.translate(language, "my_account", `DeliveryAddressV2/company`)}
                            className={`w-full px-4 py-3 border border-solid border-[#E5E5E5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#269C6E] placeholder:text-gray-500 text-sm`}
                        />
                        {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company.message}</p>}
                    </div>

                    <div id="address-phone-container">
                        <input
                            id="address-phone"
                            {...register("phone", {
                                onBlur: (e) => {
                                    const val = e.target.value;
                                    if (val && selectedCountry?.id?.toLowerCase() === 'de') {
                                        const result = validatePhoneDE(val, language);
                                        if (result.isValid && result.e164 && result.e164 !== val) {
                                            setValue("phone", result.e164);
                                        }
                                    }
                                }
                            })}
                            type="tel"
                            placeholder={`${dailingCode}1551082123`}
                            className={inputClasses("phone")}
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                        <p id="address-phone-hint" className="mt-1 text-xs text-[#9D9D9D]">{translator.translate(language, "my_account", `DeliveryAddressV2/phoneHint`)}</p>
                    </div>

                    <div id="address-default-container" className="mt-2">
                        <label id="address-default-label" className="inline-flex items-center">
                            <input
                                id="address-default"
                                {...register("is_default")}
                                type="checkbox"
                                className="!block rounded text-[#269C6E] focus:ring-[#269C6E] checked:bg-[#269C6E] checked:text-white border-solid border border-gray-300"
                            />
                            <span id="address-default-text" className="ml-2 text-[#9D9D9D] text-xs font-medium tracking-wider">
                                {translator.translate(language, "my_account", `DeliveryAddressV2/saveAsDefault`)}
                            </span>
                        </label>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default DeliveryAddressPersonalAddressForm;